import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChartNoAxesCombined, Gauge, Search, ShieldCheck, UsersRound } from "lucide-react";
import { PrimaryButton, StatusPill } from "@ocs/ui";
import { adminApi } from "../api";
import { amount, shortAddress } from "../format";

type NodeRecord = {
  position_id: string; user_address: string; position_status: number; principal_balance: string;
  original_deposit_amount: string; deposit_tx_hash: string; deposit_confirmed_at: string;
  maturity_at: string; cumulative_earning_withdrawn: string; reserved_earning_withdrawal: string;
  referral_code: string | null; direct_total_earned: string | null; direct_available: string | null;
  fee_total_earned: string | null; fee_available: string | null;
};
type UserRecord = {
  address: string;
  node: NodeRecord | null;
  market: null | {
    account_earning_stop_date: string | null; earningStopped: boolean;
    earningPaused?: boolean; earningPermanentlyStopped?: boolean;
    earning_pause_from?: string | null; earning_resume_from?: string | null;
    earning_pause_reason?: string | null;
    is_node_identity: number; static_total: string; dynamic_total: string; direct_total: string;
    team_total: string; fee_dividend_total: string; available_total: string;
    static_available: string; dynamic_available: string; reserved_total: string;
    withdrawn_total: string; sage_total: string; iage_total: string;
    normal_team_total: string; peer_total: string; position_count: number;
    active_position_count: number; earning_position_count: number; active_principal_total: string;
    position_earning_total: string; position_cap_total: string; direct_referral_count: number;
    last_settlement_date: string | null; rank_level: number | null;
    total_team_performance: string | null; small_area_performance: string | null;
    rank_reward_rate: string | null;
  };
  marketPositions: Array<{
    position_id: string; identity_type: number; product_name: string; principal_usdt_equivalent: string;
    cumulative_static_earning: string; cumulative_dynamic_earning: string; earning_cap_amount: string;
    position_status: number; started_at: string;
  }>;
  marketDaily: Array<{
    settlement_date: string; static_earning: string; dynamic_earning: string;
    direct_earning: string; team_earning: string; fee_dividend_earning: string;
  }>;
};

const percent = (part?: string | null, whole?: string | null) => {
  const denominator = Number(whole ?? 0);
  if (!denominator) return "0.00%";
  return `${Math.min(100, Number(part ?? 0) / denominator * 100).toFixed(2)}%`;
};

export function NodeSearchPage() {
  const [draft, setDraft] = useState("");
  const [address, setAddress] = useState("");
  const valid = /^0x[a-fA-F0-9]{40}$/.test(address);
  const query = useQuery({
    queryKey: ["user-detail", address],
    queryFn: () => adminApi<UserRecord>(`/users/${address}`),
    enabled: valid
  });
  const data = query.data;
  const node = data?.node;
  const market = data?.market;
  return (
    <div className="node-search-page">
      <form className="node-search" onSubmit={(event) => { event.preventDefault(); setAddress(draft.trim().toLowerCase()); }}>
        <Search size={20} /><label>钱包地址<input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="0x…" /></label><PrimaryButton>查询用户</PrimaryButton>
      </form>
      {!address && <div className="instruction-state"><strong>统一用户数据查询</strong><span>一个地址同时查看 Market 静态收益、V3 动态推广收益和原节点账本。三套口径独立展示。</span></div>}
      {address && !valid && <p className="form-error">请输入完整的 42 位 EVM 钱包地址。</p>}
      {valid && query.isLoading && <div className="admin-skeleton"><i /><i /><i /></div>}
      {valid && query.isError && (
        <div className="instruction-state">
          <strong>用户数据读取失败</strong>
          <span>{query.error instanceof Error ? query.error.message : "管理服务查询失败，请稍后重试。"}</span>
        </div>
      )}
      {data && !node && !market && <div className="instruction-state"><strong>没有业务记录</strong><span>该地址尚未参与 Market，也没有原节点仓位。</span></div>}
      {data && (node || market) && (
        <>
          <section className="node-profile">
            <div><p className="eyebrow">UNIFIED USER LEDGER</p><h2>{shortAddress(data.address)}</h2><code>{data.address}</code></div>
            <StatusPill tone={node && market ? "success" : "warning"}>{node && market ? "Market + 节点双身份" : market ? "Market 用户" : "节点用户"}</StatusPill>
          </section>

          <section className="user-ledger-groups">
            <article>
              <header><ChartNoAxesCombined /><div><span>Market 静态收益</span><small>依据 Market 需求文档逐日结算</small></div></header>
              <strong>{amount(market?.static_total)} USDT</strong>
              <dl><div><dt>静态可领取</dt><dd>{amount(market?.static_available)}</dd></div><div><dt>全账户可领取</dt><dd>{amount(market?.available_total)}</dd></div><div><dt>SAGE / IAGE</dt><dd>{amount(market?.sage_total)} / {amount(market?.iage_total)}</dd></div></dl>
            </article>
            <article>
              <header><UsersRound /><div><span>Market 动态收益</span><small>market-reward-simulator-v3</small></div></header>
              <strong>{amount(market?.dynamic_total)} USDT</strong>
              <dl><div><dt>直推 / 团队</dt><dd>{amount(market?.direct_total)} / {amount(market?.team_total)}</dd></div><div><dt>级差 / 平级</dt><dd>{amount(market?.normal_team_total)} / {amount(market?.peer_total)}</dd></div><div><dt>可领取 / 当前等级</dt><dd>{amount(market?.dynamic_available)} / V{market?.rank_level ?? 0}</dd></div></dl>
            </article>
            <article>
              <header><ShieldCheck /><div><span>原节点账本</span><small>原系统数据，只读，不改逻辑</small></div></header>
              <strong>{node ? `${amount(node.principal_balance)} USDT` : "无节点"}</strong>
              <dl><div><dt>直推累计 / 可用</dt><dd>{amount(node?.direct_total_earned)} / {amount(node?.direct_available)}</dd></div><div><dt>分红累计 / 可用</dt><dd>{amount(node?.fee_total_earned)} / {amount(node?.fee_available)}</dd></div><div><dt>Market 节点分红</dt><dd>{amount(market?.fee_dividend_total)}</dd></div></dl>
            </article>
            <article>
              <header><Gauge /><div><span>Market 用户监控</span><small>仓位、上限与领取状态</small></div>{market?.earningStopped && <StatusPill tone="warning">{market.earningPaused && !market.earningPermanentlyStopped ? "收益暂停，可恢复" : "账户已停益"}</StatusPill>}</header>
              <strong>{amount(market?.active_principal_total)} USDT</strong>
              <dl><div><dt>仓位 / 收益中</dt><dd>{market?.position_count ?? 0} / {market?.earning_position_count ?? 0}</dd></div>{market?.account_earning_stop_date && <div><dt>停益生效日</dt><dd>{market.account_earning_stop_date}</dd></div>}<div><dt>3 倍上限使用率</dt><dd>{percent(market?.position_earning_total, market?.position_cap_total)}</dd></div><div><dt>待确认 / 已领取</dt><dd>{amount(market?.reserved_total)} / {amount(market?.withdrawn_total)}</dd></div><div><dt>有效直推人数</dt><dd>{market?.direct_referral_count ?? 0}</dd></div></dl>
              {market?.earning_pause_from && <dl><div><dt>暂停生效日</dt><dd>{market.earning_pause_from}</dd></div><div><dt>恢复收益日</dt><dd>{market.earning_resume_from ?? "待通知"}</dd></div>{market.earning_pause_reason && <div><dt>暂停原因</dt><dd>{market.earning_pause_reason}</dd></div>}</dl>}
            </article>
          </section>

          {data.marketPositions.length > 0 && <section className="node-timeline"><div><span>Market 仓位</span><strong>{data.marketPositions.length} 个</strong></div>{data.marketPositions.slice(0, 8).map((position) => <div key={position.position_id}><span>{position.product_name} · {position.identity_type === 2 ? "节点接入" : "普通参与"}</span><strong>{amount(position.principal_usdt_equivalent)} USDT</strong><code>静态 {amount(position.cumulative_static_earning)} / 动态 {amount(position.cumulative_dynamic_earning)} / 上限 {amount(position.earning_cap_amount)}</code></div>)}</section>}

          {data.marketDaily.length > 0 && <section className="node-timeline"><div><span>最近 30 日 Market 收益</span><strong>静态 / 动态分列</strong><code>动态仅含直推奖、级差奖和平级奖，不含节点手续费分红</code></div>{data.marketDaily.map((day) => <div key={day.settlement_date}><span>{String(day.settlement_date).slice(0, 10)}</span><strong>{amount(day.static_earning)} / {amount(day.dynamic_earning)} USDT</strong><code>直推 {amount(day.direct_earning)} · 团队 {amount(day.team_earning)} · 节点分红 {amount(day.fee_dividend_earning)}</code></div>)}</section>}

          {node && <section className="node-timeline"><div><span>原节点入金确认</span><strong>{new Date(node.deposit_confirmed_at).toLocaleString("zh-CN", { hour12: false })}</strong><code>{node.deposit_tx_hash}</code></div><div><span>180 天到期</span><strong>{new Date(node.maturity_at).toLocaleString("zh-CN", { hour12: false })}</strong></div><div><span>节点邀请码</span><strong>{node.referral_code ?? "尚未生成"}</strong></div></section>}
        </>
      )}
    </div>
  );
}
